import React, { useEffect, useState } from "react";
import {
  Card,
  Row,
  Col,
  Table,
  Badge,
  Spinner,
  Alert,
  Form,
  Button,
} from "react-bootstrap";
import { utils, writeFile } from "xlsx";

const SubAdminSubscriptions = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [subscribers, setSubscribers] = useState([]);
  const [roleFilter, setRoleFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");

  const formatDateDMY = (iso) => {
    if (!iso) return "—";
    try {
      const d = new Date(iso);
      if (isNaN(d.getTime())) return "—";
      const dd = String(d.getDate()).padStart(2, "0");
      const mm = String(d.getMonth() + 1).padStart(2, "0");
      const yyyy = d.getFullYear();
      return `${dd}/${mm}/${yyyy}`;
    } catch (_) {
      return "—";
    }
  };

  const formatCurrency = (amount) => {
    if (!amount) return "₹0";
    return `₹${parseFloat(amount).toFixed(2)}`;
  };

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        // Construct the full URL to the backend server
        // Since the backend is running on port 3000, we need to make requests directly to it
        // instead of relying on the proxy which seems to not be working correctly
        const baseUrl =
          process.env.REACT_APP_API_BASE_URL || "https://api.quvikme.com";
        const res = await fetch(`${baseUrl}/api/subscriptions`, {
          headers: {
            Authorization: `Bearer ${sessionStorage.getItem("adminToken")}`,
          },
        });

        if (!res.ok) {
          const errorText = await res.text();
          throw new Error(
            `Failed to load subscribers: ${res.status} ${errorText}`
          );
        }

        const data = await res.json();
        console.debug("[SubAdminSubscriptions] Loaded data:", data);

        const combined = Array.isArray(data.subscriptions)
          ? data.subscriptions.map((s) => ({
              ...s,
              _formatDate: formatDateDMY,
            }))
          : [];

        setSubscribers(combined);
      } catch (e) {
        console.error("[SubAdminSubscriptions] Failed", e);
        setError(e.message || "Failed to load subscribers");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const filtered = subscribers.filter((s) => {
    const roleOk = roleFilter === "all" || s.role.toLowerCase() === roleFilter;
    const typeOk =
      typeFilter === "all" ||
      (s.type || "").toString().toLowerCase() === typeFilter;
    return roleOk && typeOk;
  });

  // Calculate summary statistics
  const totalSubscriptions = filtered.length;
  const userSubscriptions = filtered.filter((s) => s.role === "User").length;
  const vendorSubscriptions = filtered.filter(
    (s) => s.role === "Vendor"
  ).length;

  // Calculate total revenue
  const totalRevenue = filtered.reduce((sum, subscriber) => {
    return sum + (parseFloat(subscriber.price) || 0);
  }, 0);

  const exportToExcel = () => {
    // Prepare data for export
    const exportData = filtered.map((subscriber) => ({
      Role: subscriber.role,
      Name: subscriber.name,
      Plan: subscriber.type,
      Billing: subscriber.billing,
      Price: formatCurrency(subscriber.price),
      Pincode: subscriber.pincode,
      "Society Name": subscriber.societyName,
      "Start Date": subscriber._formatDate(subscriber.start),
      "End Date": subscriber._formatDate(subscriber.end),
      Status: subscriber.status,
    }));

    // Create worksheet
    const ws = utils.json_to_sheet(exportData);

    // Create workbook
    const wb = utils.book_new();
    utils.book_append_sheet(wb, ws, "Local Subscribers");

    // Export to Excel
    writeFile(wb, "local-subscribers.xlsx");
  };

  if (loading) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ height: "40vh" }}
      >
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="danger">
        <Alert.Heading>Error</Alert.Heading>
        <p>{error}</p>
      </Alert>
    );
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h1 className="mb-0">Local Subscribers</h1>
        <div className="d-flex gap-2">
          <Button variant="success" onClick={exportToExcel}>
            Download Excel
          </Button>
          <Form.Select
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value)}
            style={{ width: 160 }}
          >
            <option value="all">All Roles</option>
            <option value="user">Users</option>
            <option value="vendor">Vendors</option>
          </Form.Select>
          <Form.Select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            style={{ width: 180 }}
          >
            <option value="all">All Types</option>
            <option value="none">none</option>
            <option value="basic">basic</option>
            <option value="standard">standard</option>
            <option value="premium">premium</option>
          </Form.Select>
        </div>
      </div>

      {/* Summary Cards */}
      <Row className="mb-4">
        <Col md={3}>
          <Card className="bg-primary text-white">
            <Card.Body>
              <Card.Title>Total Subscriptions</Card.Title>
              <Card.Text className="display-4">{totalSubscriptions}</Card.Text>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="bg-success text-white">
            <Card.Body>
              <Card.Title>User Subscriptions</Card.Title>
              <Card.Text className="display-4">{userSubscriptions}</Card.Text>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="bg-info text-white">
            <Card.Body>
              <Card.Title>Vendor Subscriptions</Card.Title>
              <Card.Text className="display-4">{vendorSubscriptions}</Card.Text>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="bg-warning text-white">
            <Card.Body>
              <Card.Title>Total Revenue</Card.Title>
              <Card.Text className="display-4">
                {formatCurrency(totalRevenue)}
              </Card.Text>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Card>
        <Card.Header>
          <h5 className="mb-0">Subscribers in Your Area ({filtered.length})</h5>
        </Card.Header>
        <Card.Body>
          <Table responsive hover>
            <thead>
              <tr>
                <th>Role</th>
                <th>Name</th>
                <th>Plan</th>
                <th>Billing</th>
                <th>Price</th>
                <th>Pincode</th>
                <th>Society Name</th>
                <th>Start</th>
                <th>End</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((s) => (
                <tr key={`${s.role}-${s.id}`}>
                  <td>
                    <Badge bg={s.role === "Vendor" ? "info" : "secondary"}>
                      {s.role}
                    </Badge>
                  </td>
                  <td>{s.name}</td>
                  <td>
                    <Badge
                      bg={
                        s.type === "premium"
                          ? "success"
                          : s.type === "standard"
                          ? "info"
                          : s.type === "basic"
                          ? "primary"
                          : s.type === "none"
                          ? "warning"
                          : "secondary"
                      }
                    >
                      {s.type || "—"}
                    </Badge>
                  </td>
                  <td>
                    <Badge
                      bg={
                        s.billing === "yearly"
                          ? "success"
                          : s.billing === "quarterly"
                          ? "info"
                          : s.billing === "monthly"
                          ? "primary"
                          : s.billing === "30 days"
                          ? "warning"
                          : "secondary"
                      }
                    >
                      {s.billing || "—"}
                    </Badge>
                  </td>
                  <td>{formatCurrency(s.price)}</td>
                  <td>{s.pincode}</td>
                  <td>{s.societyName || "—"}</td>
                  <td>{s._formatDate(s.start)}</td>
                  <td>{s._formatDate(s.end)}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Card.Body>
      </Card>
    </div>
  );
};

export default SubAdminSubscriptions;
